import type { BadgeProps } from "@bowfast/ui/components/badge";
import type { LucideIcon } from "lucide-react";
import { CheckCircle2Icon, ClockIcon, XCircleIcon } from "lucide-react";
import type {
  ExpeditionStatus as ApiExpeditionStatus,
  CrewProfileQuery,
  OrgCrewListQuery,
  OrgRole,
} from "~/graphql/generated";
import type { StatItem } from "~/lib/types";

export type CrewListMember = NonNullable<OrgCrewListQuery["organisation"]>["members"][number];
export type CrewProfileUser = NonNullable<CrewProfileQuery["user"]>;
export type TabKey = "all" | "at-sea" | "skippers" | "instructors" | "crew";

export interface MetricPill extends StatItem {
  color: string;
}

// ── Crew detail view types ────────────────────────────────────────────────────

export type CertStatus = "issued" | "expired" | "draft";
export type LocalExpStatus = "active" | "completed" | "planning" | "open";

export interface Certificate {
  name: string;
  issuer: string;
  issuedDate: string;
  expiryDate?: string;
  status: CertStatus;
}

export interface Expedition {
  name: string;
  role: string;
  dates: string;
  distance: string;
  status: LocalExpStatus;
  vessel: string;
}

export interface MilesEntry {
  year: number;
  nm: number;
  expeditions: number;
}

/** UI view model assembled from CrewProfile + Certificates + MilesAwards queries */
export interface CrewProfileView {
  id: string;
  name: string;
  initials: string;
  role: OrgRole;
  email: string;
  location: string;
  bio: string;
  joinedDate: string;
  gradient: string;
  totalMiles: number;
  totalExpeditions: number;
  totalCerts: number;
  certificates: Certificate[];
  expeditions: Expedition[];
  milesLog: MilesEntry[];
}

export const API_EXP_STATUS_MAP: Record<ApiExpeditionStatus, LocalExpStatus> = {
  ACTIVE: "active",
  COMPLETED: "completed",
  PLANNING: "planning",
  OPEN: "open",
  FULLY_BOOKED: "open",
  CANCELLED: "completed",
};

export const CERT_STATUS_CONFIG: Record<CertStatus, { label: string; Icon: LucideIcon; color: string }> = {
  issued: { label: "Issued", Icon: CheckCircle2Icon, color: "text-shallow-600 dark:text-shallow-400" },
  expired: { label: "Expired", Icon: XCircleIcon, color: "text-coral-500" },
  draft: { label: "Draft", Icon: ClockIcon, color: "text-muted-foreground" },
};

export const EXP_STATUS_VARIANT: Record<LocalExpStatus, BadgeProps["variant"]> = {
  active: "active",
  completed: "completed",
  planning: "planning",
  open: "open",
};

export function mapCertStatus(_issuedAt: string, revokedAt: string | null | undefined): CertStatus {
  if (revokedAt) return "expired";
  return "issued";
}

export function mapExpeditionDates(dep?: string | null, ret?: string | null): string {
  const fmt = (d: string) => new Date(d).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  if (dep && ret) return `${fmt(dep)} – ${fmt(ret)}`;
  if (dep) return `From ${fmt(dep)}`;
  return "Dates TBC";
}
